<template>
  <div>
    <h3 class="mb-4"><strong>{{ $t('on_boarding.download_gpx_page.download_gpx') }}</strong></h3>

    <div class="form-group mb-3">
      <p><label class="m-0 text-yellow">{{ $t('on_boarding.download_gpx_page.choose_file_type') }}</label></p>
      <p class="p-2 bg-dark w-100 rounded">
        <select class="custom-select w-100" v-model="selectedOption">
          <option selected value="gpx">GPX</option>
          <option value="kml">KML</option>
          <option value="geojson">GeoJSON</option>
        </select>
      </p>
    </div>

    <button class="btn btn-primary" @click="downloadClicked" :disabled="loading">
      {{ loading ? $t('on_boarding.download_gpx_page.downloading') : $t('shared.download') }}
    </button>
    <LoadingDialog :show-loading-dialog="loading" />
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import LoadingDialog from '../../../utils/shared_components/LoadingDialog.vue';
import graph_client from '../../../utils/graphql/graph_client';
import { GetHike } from '../../../utils/graphql/hikes-graph';
import { geojsonToGpx } from '../../../utils/geojsonToGpx';

export default defineComponent({
  name: 'OnBoardingDownloadGPXPage',
  components: { LoadingDialog },
  data() {
    return {
      selectedOption: 'gpx',
      loading: false,
    };
  },
  computed: {
    hikeId() {
      return this.$store.state.onBoarding.hikeId;
    },
  },
  methods: {
    async downloadClicked() {
      this.loading = true;
      try {
        const downloaded = await this.downloadViaGraphql(this.selectedOption);
        if (downloaded) {
          this.$store.commit('onBoarding/pushPage', 'Success');
        }
      } catch (error) {
        console.error('Download failed', error);
        alert(this.$t('on_boarding.download_gpx_page.could_not_download_file'));
      } finally {
        this.loading = false;
      }
    },
    async downloadViaGraphql(formatId) {
      if (!this.hikeId) {
        throw new Error('No hike ID available for download');
      }

      const response = await graph_client.request(GetHike, { id: this.hikeId });
      const hike = response?.hike;
      if (!hike) {
        throw new Error('Hike not found');
      }

      const { lineFeatures, metadata, fileName } = this.buildHikeContext(hike);

      if (!lineFeatures.length) {
        window.alert(this.$t('on_boarding.download_gpx_page.no_format_data', { format: formatId.toUpperCase() }));
        return false;
      }

      const featureCollection = { type: 'FeatureCollection', features: lineFeatures };

      if (formatId === 'gpx') {
        const gpx = geojsonToGpx(featureCollection, null, {
          ...metadata,
          outputType: 'rte',
          routeName: metadata.metadata?.name || this.$t('on_boarding.download_gpx_page.hike'),
          mergeRoutesByProperty: 'hikeDayNumber',
        });
        this.triggerDownload(new Blob([gpx], { type: 'application/gpx+xml' }), fileName);
      } else if (formatId === 'kml') {
        const kml = this.toKml(lineFeatures, metadata.metadata?.name || this.$t('on_boarding.download_gpx_page.trail'));
        this.triggerDownload(
          new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' }),
          fileName.replace(/\.gpx$/i, '.kml')
        );
      } else if (formatId === 'geojson') {
        this.triggerDownload(
          new Blob([JSON.stringify(featureCollection)], { type: 'application/geo+json' }),
          fileName.replace(/\.gpx$/i, '.geojson')
        );
      }

      return true;
    },
    buildHikeContext(hike) {
      const metadata = { creator: 'Hiiker', metadata: {} };
      const title = hike?.name || hike?.title || hike?.slug;
      if (title) metadata.metadata.name = title;
      if (hike?.createdAt) metadata.metadata.time = hike.createdAt;

      const geojson = this.parseHikeGeojson(
        hike?.geojsonMultilinestringsWithStartEndPointsString ||
        hike?.geojsonRoutesOnlyString
      );

      return {
        lineFeatures: this.collectLineFeatures(geojson),
        metadata,
        fileName: this.composeFileName(title || 'hike'),
      };
    },
    composeFileName(label) {
      const slug = (label || 'hiiker')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase() || 'hiiker';
      return `${slug}.gpx`;
    },
    parseHikeGeojson(geojsonLike) {
      if (!geojsonLike) return null;
      if (typeof geojsonLike === 'string') {
        try {
          return JSON.parse(geojsonLike);
        } catch (error) {
          console.error('Failed to parse hike geojson string', error);
          return null;
        }
      }
      return geojsonLike?.type === 'FeatureCollection' ? geojsonLike : null;
    },
    collectLineFeatures(geojson) {
      if (!geojson || !Array.isArray(geojson.features)) return [];
      const results = [];
      geojson.features.forEach((feature) => {
        const geometry = feature?.geometry;
        if (!geometry) return;
        if (geometry.type === 'LineString' && Array.isArray(geometry.coordinates) && geometry.coordinates.length > 1) {
          results.push({ type: 'Feature', properties: { ...(feature.properties || {}) }, geometry });
        } else if (geometry.type === 'MultiLineString') {
          (geometry.coordinates || []).forEach((line) => {
            if (Array.isArray(line) && line.length > 1) {
              results.push({
                type: 'Feature',
                properties: { ...(feature.properties || {}) },
                geometry: { type: 'LineString', coordinates: line },
              });
            }
          });
        }
      });
      return results;
    },
    triggerDownload(blob, filename) {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    },
    toKml(lineFeatures, documentName) {
      const escaped = (s) => (s || '').replace(/[&<>'"]/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&apos;', '"': '&quot;' }[c]
      ));
      const placemarks = lineFeatures.map((feature, index) => {
        const coords = (feature?.geometry?.coordinates || [])
          .map(([lng, lat, ele]) => `${lng},${lat},${Number.isFinite(ele) ? ele : 0}`)
          .join(' ');
        const name = feature?.properties?.name || this.$t('on_boarding.download_gpx_page.segment_name', { name: documentName, index: index + 1 });
        return `<Placemark><name>${escaped(name)}</name><LineString><tessellate>1</tessellate><coordinates>${coords}</coordinates></LineString></Placemark>`;
      }).join('');
      return `<?xml version="1.0" encoding="UTF-8"?><kml xmlns="http://www.opengis.net/kml/2.2"><Document><name>${escaped(documentName)}</name>${placemarks}</Document></kml>`;
    },
  },
});
</script>

<style scoped>
</style>
